import { materializeIfLazy } from '../materialize.ts';
import type { Dataset, MaterializedDataset } from '../value.ts';
import { useEvalDeps } from './EvalDepsContext.ts';
import { useSession } from './SessionContext.ts';
import { buildEvalContext } from './useEvalExpr.ts';

/**
 * React hook returning a bound materializer
 * `(ds: Dataset) => Promise<MaterializedDataset>`. Already-materialized
 * datasets short-circuit to an immediately-resolved promise; lazy
 * datasets go through `materialize()`, which is itself memoised on
 * `session.fetch` so repeat calls for the same plan coalesce.
 *
 * The returned function captures the current session + `EvalDeps`; if
 * either changes the hook re-runs and returns a fresh closure. Inside
 * a stable render the closure reference is stable (React Compiler).
 *
 * For "evaluate an expression and materialise it in one step" — the
 * typical pattern inside a tool's `render` — prefer
 * {@link useMaterializedExpr}. Use this hook when the caller already
 * holds a `Dataset` reference (e.g., from a previous eval) and wants
 * to force it on demand.
 *
 * @throws via `useSession()` if no `<SessionContext>` provider is
 *   mounted upstream.
 */
export function useMaterialize(): (ds: Dataset) => Promise<MaterializedDataset> {
  const session = useSession();
  const deps = useEvalDeps();
  return (ds: Dataset): Promise<MaterializedDataset> =>
    materializeIfLazy(ds, buildEvalContext(session, deps), deps.ops, deps.verbs);
}
